import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ficheClientService from '../services/ficheClientService';
import logo from '../assets/img/logo.png';

const FicheClientForm = () => {
  const navigate = useNavigate();
  const { token: urlToken } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [token, setToken] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);

  // Validation du token au chargement
  useEffect(() => {
    const validateTokenAndLoadData = async () => {
      if (!urlToken) {
        toast.error('Token manquant. Accès non autorisé.');
        setIsValidatingToken(false);
        return;
      }

      setToken(urlToken);

      try {
        const response = await ficheClientService.validateToken(urlToken);

        console.log('🔍 Réponse validation token:', response);
        console.log('🔍 Client data:', response.client);

        if (response.valid && response.client) {
          setTokenValid(true);
          setClientInfo(response.client);

          // Pré-remplir les données du client
          const client = response.client;

          console.log('📝 Pré-remplissage avec:', {
            nomEntreprise: client.companyName,
            siret: client.siret,
            adresse: client.address,
            postCode: client.postCode,
            town: client.town,
            tva: client.TVA,
            contactNom: `${client.contactFirstname} ${client.contactLastname}`,
            contactEmail: client.contactEMail,
            contactTelephone: client.contactPhone,
            addressFacturation: client.addressFacturation,
            postCodeFacturation: client.postCodeFacturation,
            townFacturation: client.townFacturation,
          });

          // Construire l'adresse du siège social
          let adresseSiege = client.address || '';
          if (client.postCode || client.town) {
            const parts = [];
            if (adresseSiege) parts.push(adresseSiege);
            if (client.postCode) parts.push(client.postCode);
            if (client.town) parts.push(client.town);
            adresseSiege = parts.join(', ');
          }

          // Construire l'adresse de facturation
          let adresseFacturation = '';
          if (client.addressFacturation || client.postCodeFacturation || client.townFacturation) {
            const parts = [];
            if (client.addressFacturation) parts.push(client.addressFacturation);
            if (client.postCodeFacturation) parts.push(client.postCodeFacturation);
            if (client.townFacturation) parts.push(client.townFacturation);
            adresseFacturation = parts.join(', ');
          }

          setFormData(prev => {
            const newData = {
              ...prev,
              // Informations entreprise
              nomEntreprise: client.companyName || '',
              siret: client.siret || '',
              adresse: adresseSiege,
              codePostal: client.postCode || '',
              ville: client.town || '',
              tvaIntracommunautaire: client.TVA || '',
              formeJuridique: client.formeJuridique || '',

              // Adresse de facturation
              adresseFacturation: adresseFacturation,
              codePostalFacturation: client.postCodeFacturation || '',
              villeFacturation: client.townFacturation || '',

              // Contact
              contactNom: `${client.contactFirstname || ''} ${client.contactLastname || ''}`.trim(),
              contactPoste: client.contactPoste || '',
              contactEmail: client.contactEMail || '',
              contactTelephone: client.contactPhone || '',
            };

            console.log('✅ FormData après pré-remplissage:', newData);
            return newData;
          });

          toast.success('Formulaire chargé avec succès ! Les informations de votre entreprise ont été pré-remplies.');
        }
      } catch (error) {
        console.error('Erreur validation token:', error);
        toast.error(error.response?.data?.message || 'Token invalide ou expiré');
        setTokenValid(false);
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateTokenAndLoadData();
  }, [urlToken]);

  const [formData, setFormData] = useState({
    // Étape 1: Informations entreprise
    nomEntreprise: '',
    siret: '',
    formeJuridique: '',
    adresse: '',
    codePostal: '',
    ville: '',
    adresseFacturation: '',
    codePostalFacturation: '',
    villeFacturation: '',
    tvaIntracommunautaire: '',
    conventionCollective: '',
    centreVisiteMedicale: '',
    contactNom: '',
    contactPoste: '',
    contactEmail: '',
    contactTelephone: '',
    
    // Signature et attestation
    signatureNom: '',
    signatureFonction: '',
    signatureDate: new Date().toISOString().split('T')[0],
    signature: '',
    
    // Étape 2: Postes (tableau)
    postes: [{
      id: 1,
      intitule: '',
      tauxHoraire: '',
      heuresParSemaine: '35',
      tauxHS: [],
      majorationNuit: '',
      heuresNuit: '',
      majorationDimanche: '',
      autresMajorations: '',
      primes: [],
      avantages: '',
      epi: {
        casque: false,
        chaussuresSecurite: false,
        gants: false,
        lunettes: false,
        masque: false,
        bouchonsOreilles: false,
        harnais: false,
        giletHauteVisibilite: false,
        autre: ''
      },
      formationsHabilitations: '',
      machinesOutillages: '',
      surveillanceMedicale: '',
      penibilite: {
        milieuHyperbare: false,
        temperaturesExtremes: false,
        bruit: false,
        travailNuit: false,
        equipesSuccessives: false,
        travailRepetitif: false,
      }
    }]
  });

  // Gestion des étapes
  const handleNext = () => {
    if (currentStep === 1) {
      // Validation étape 1
      if (!formData.nomEntreprise || !formData.siret || !formData.contactEmail) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
    }
    if (currentStep === 2) {
      // Validation étape 2
      const hasValidPoste = formData.postes.some(p => p.intitule && p.tauxHoraire);
      if (!hasValidPoste) {
        toast.error('Veuillez renseigner au moins un poste avec intitulé et taux horaire');
        return;
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Gestion des postes
  const addPoste = () => {
    const newPoste = {
      id: Date.now(),
      intitule: '',
      tauxHoraire: '',
      heuresParSemaine: '35',
      tauxHS: [],
      majorationNuit: '',
      heuresNuit: '',
      majorationDimanche: '',
      autresMajorations: '',
      primes: [],
      avantages: '',
      epi: {
        casque: false,
        chaussuresSecurite: false,
        gants: false,
        lunettes: false,
        masque: false,
        bouchonsOreilles: false,
        harnais: false,
        giletHauteVisibilite: false,
        autre: ''
      },
      formationsHabilitations: '',
      machinesOutillages: '',
      surveillanceMedicale: '',
      penibilite: {
        milieuHyperbare: false,
        temperaturesExtremes: false,
        bruit: false,
        travailNuit: false,
        equipesSuccessives: false,
        travailRepetitif: false,
      }
    };
    setFormData(prev => ({
      ...prev,
      postes: [...prev.postes, newPoste]
    }));
  };

  const removePoste = (index) => {
    if (formData.postes.length === 1) {
      toast.error('Vous devez avoir au moins un poste');
      return;
    }
    setFormData(prev => ({
      ...prev,
      postes: prev.postes.filter((_, i) => i !== index)
    }));
  };

  const updatePoste = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      postes: prev.postes.map((poste, i) =>
        i === index ? { ...poste, [field]: value } : poste
      )
    }));
  };

  const updatePenibilite = (posteIndex, key) => {
    setFormData(prev => ({
      ...prev,
      postes: prev.postes.map((poste, i) =>
        i === posteIndex
          ? { ...poste, penibilite: { ...poste.penibilite, [key]: !poste.penibilite[key] } }
          : poste
      )
    }));
  };

  // Gestion des heures supplémentaires
  const addTauxHS = (posteIndex) => {
    const newTauxHS = { id: Date.now(), tranche: '', taux: '' };
    setFormData(prev => ({
      ...prev,
      postes: prev.postes.map((poste, i) =>
        i === posteIndex
          ? { ...poste, tauxHS: [...poste.tauxHS, newTauxHS] }
          : poste
      )
    }));
  };

  const removeTauxHS = (posteIndex, hsIndex) => {
    setFormData(prev => ({
      ...prev,
      postes: prev.postes.map((poste, i) =>
        i === posteIndex
          ? { ...poste, tauxHS: poste.tauxHS.filter((_, idx) => idx !== hsIndex) }
          : poste
      )
    }));
  };

  const updateTauxHS = (posteIndex, hsIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      postes: prev.postes.map((poste, i) =>
        i === posteIndex
          ? {
              ...poste,
              tauxHS: poste.tauxHS.map((hs, idx) =>
                idx === hsIndex ? { ...hs, [field]: value } : hs
              )
            }
          : poste
      )
    }));
  };

  // Gestion des primes
  const addPrime = (posteIndex) => {
    const newPrime = { id: Date.now(), nom: '', montant: '', frequence: '', assujettissement: '' };
    setFormData(prev => ({
      ...prev,
      postes: prev.postes.map((poste, i) =>
        i === posteIndex
          ? { ...poste, primes: [...poste.primes, newPrime] }
          : poste
      )
    }));
  };

  const removePrime = (posteIndex, primeIndex) => {
    setFormData(prev => ({
      ...prev,
      postes: prev.postes.map((poste, i) =>
        i === posteIndex
          ? { ...poste, primes: poste.primes.filter((_, idx) => idx !== primeIndex) }
          : poste
      )
    }));
  };

  const updatePrime = (posteIndex, primeIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      postes: prev.postes.map((poste, i) =>
        i === posteIndex
          ? {
              ...poste,
              primes: poste.primes.map((prime, idx) =>
                idx === primeIndex ? { ...prime, [field]: value } : prime
              )
            }
          : poste
      )
    }));
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    // Validation finale
    if (!formData.signatureNom || !formData.signatureFonction || !formData.signature) {
      toast.error('Veuillez remplir tous les champs de signature');
      return;
    }

    setIsSubmitting(true);

    try {
      // Transformer les données pour correspondre au format backend
      const postesTransformes = formData.postes.map(poste => {
        // Transformer tauxHS en heuresSupplementaires
        const heuresSupplementaires = poste.tauxHS.map(hs => {
          // Si c'est déjà au format de/a/taux, le garder tel quel
          if (hs.de && hs.a && hs.taux) {
            return { de: hs.de, a: hs.a, taux: hs.taux };
          }
          // Sinon, essayer de parser la tranche (ex: "8 premières heures" -> de: "0", a: "8")
          // Pour l'instant, on garde le format tranche/taux
          return {
            de: '',
            a: '',
            taux: hs.taux || ''
          };
        });

        // Transformer les primes pour avoir nom, montant, frequence, assujettissement
        const primesTransformees = poste.primes.map(prime => ({
          nom: prime.nom || '',
          montant: prime.montant || '',
          frequence: prime.frequence || '',
          assujettissement: prime.assujettissement || ''
        }));

        // Transformer la pénibilité du format formulaire vers le format backend
        const penibilite = {
          manutentionChargesLourdes: poste.penibilite.milieuHyperbare || false,
          posturesPenibles: false,
          vibrationsManiques: false,
          agentsChimiques: false,
          temperatureExtreme: poste.penibilite.temperaturesExtremes || false,
          bruit: poste.penibilite.bruit || false
        };

        return {
          intitule: poste.intitule,
          tauxHoraire: poste.tauxHoraire,
          heuresParSemaine: poste.heuresParSemaine,
          heuresSupplementaires: heuresSupplementaires,
          majorationNuit: poste.majorationNuit || '',
          majorationDimanche: poste.majorationDimanche || '',
          autreMajoration: poste.autresMajorations || poste.autreMajoration || '',
          primes: primesTransformees,
          avantages: poste.avantages || '',
          epi: {
            casque: poste.epi.casque || false,
            chaussuresSecurite: poste.epi.chaussuresSecurite || false,
            gants: poste.epi.gants || false,
            lunettes: poste.epi.lunettes || false,
            giletHauteVisibilite: poste.epi.giletHauteVisibilite || false,
            protectionAuditive: poste.epi.bouchonsOreilles || false,
            masque: poste.epi.masque || false,
            harnais: poste.epi.harnais || false,
            autre: poste.epi.autre || ''
          },
          formationsHabilitations: poste.formationsHabilitations || '',
          machinesOutillages: poste.machinesOutillages || '',
          surveillanceMedicale: poste.surveillanceMedicale || '',
          penibilite: penibilite
        };
      });

      const dataToSend = {
        nomEntreprise: formData.nomEntreprise,
        siret: formData.siret,
        formeJuridique: formData.formeJuridique || '',
        tvaIntracommunautaire: formData.tvaIntracommunautaire || '',
        adresse: formData.adresse || '',
        codePostal: formData.codePostal || '',
        ville: formData.ville || '',
        adresseFacturation: formData.adresseFacturation || '',
        codePostalFacturation: formData.codePostalFacturation || '',
        villeFacturation: formData.villeFacturation || '',
        conventionCollective: formData.conventionCollective || '',
        centreVisiteMedicale: formData.centreVisiteMedicale || '',
        contactNom: formData.contactNom || '',
        contactPoste: formData.contactPoste || '',
        contactEmail: formData.contactEmail || '',
        contactTelephone: formData.contactTelephone || '',
        postes: postesTransformes,
        signatureNom: formData.signatureNom,
        signatureFonction: formData.signatureFonction,
        signatureDate: formData.signatureDate,
        signature: formData.signature,
        dateCreation: new Date().toISOString()
      };

      // Soumettre avec le token
      await ficheClientService.submitWithToken(token, dataToSend);

      toast.success('Fiche client envoyée avec succès ! Vous allez recevoir un email de confirmation.');

      // Redirection après 2 secondes
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);

    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi de la fiche. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Affichage pendant la validation du token
  if (isValidatingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-700">Validation du token en cours...</p>
        </div>
      </div>
    );
  }

  // Affichage si le token est invalide
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-2xl p-8 text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-3xl font-bold text-red-600 mb-4">Accès Refusé</h1>
          <p className="text-gray-700 mb-6">
            Le lien que vous avez utilisé est invalide ou a expiré.
          </p>
          <p className="text-sm text-gray-600 mb-6">
            Veuillez contacter J'M Interim pour obtenir un nouveau lien d'accès.
          </p>
          <a
            href="mailto:contact@jm-interim.com"
            className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition"
          >
            Contacter J'M Interim
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={logo} alt="J'M Interim Logo" className="h-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Fiche d'Information Client
          </h1>
          <p className="text-gray-600">
            Merci de remplir ce formulaire pour nous permettre de mieux vous servir
          </p>
        </div>

        {/* Indicateur d'étapes */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition ${
                      currentStep >= step
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step}
                  </div>
                  <span className="text-sm mt-2 text-gray-600">
                    {step === 1 && 'Entreprise'}
                    {step === 2 && 'Postes'}
                    {step === 3 && 'Validation'}
                  </span>
                </div>
                {step < 3 && (
                  <div
                    className={`w-24 h-1 mx-2 transition ${
                      currentStep > step ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* ÉTAPE 1: Informations Entreprise */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">🏢 Informations Entreprise</h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'entreprise *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: ACME Corporation"
                    value={formData.nomEntreprise}
                    onChange={(e) => setFormData({...formData, nomEntreprise: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SIRET *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: 123 456 789 00012"
                    value={formData.siret}
                    onChange={(e) => setFormData({...formData, siret: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Forme juridique
                  </label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    value={formData.formeJuridique}
                    onChange={(e) => setFormData({...formData, formeJuridique: e.target.value})}
                  >
                    <option value="">-- Sélectionnez --</option>
                    <option value="SARL">SARL</option>
                    <option value="SAS">SAS</option>
                    <option value="SA">SA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                    <option value="Association">Association</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    TVA intracommunautaire
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: FR12345678901"
                    value={formData.tvaIntracommunautaire}
                    onChange={(e) => setFormData({...formData, tvaIntracommunautaire: e.target.value})}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse du siège social (rue)
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: 123 Rue de la République"
                    value={formData.adresse}
                    onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code postal
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: 75001"
                    value={formData.codePostal}
                    onChange={(e) => setFormData({...formData, codePostal: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ville
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Paris"
                    value={formData.ville}
                    onChange={(e) => setFormData({...formData, ville: e.target.value})}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse de facturation - Rue (si différente)
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: 456 Avenue des Champs"
                    value={formData.adresseFacturation}
                    onChange={(e) => setFormData({...formData, adresseFacturation: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code postal facturation
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: 75008"
                    value={formData.codePostalFacturation}
                    onChange={(e) => setFormData({...formData, codePostalFacturation: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ville facturation
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Paris"
                    value={formData.villeFacturation}
                    onChange={(e) => setFormData({...formData, villeFacturation: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Convention collective
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Métallurgie, BTP..."
                    value={formData.conventionCollective}
                    onChange={(e) => setFormData({...formData, conventionCollective: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Centre de visite médicale
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Centre médical du travail..."
                    value={formData.centreVisiteMedicale}
                    onChange={(e) => setFormData({...formData, centreVisiteMedicale: e.target.value})}
                  />
                </div>
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">👤 Contact Principal</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du contact
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Ex: Jean Dupont"
                      value={formData.contactNom}
                      onChange={(e) => setFormData({...formData, contactNom: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Poste/Fonction
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Ex: Directeur RH"
                      value={formData.contactPoste}
                      onChange={(e) => setFormData({...formData, contactPoste: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Ex: contact@entreprise.com"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Ex: 01 23 45 67 89"
                      value={formData.contactTelephone}
                      onChange={(e) => setFormData({...formData, contactTelephone: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ÉTAPE 2: Postes */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">💼 Postes à pourvoir</h2>

              {formData.postes.map((poste, index) => (
                <div key={poste.id} className="border-2 border-indigo-200 rounded-xl p-6 bg-indigo-50/30 relative">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-indigo-900">
                      Poste #{index + 1}
                    </h3>
                    {formData.postes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePoste(index)}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                      >
                        ✕ Supprimer ce poste
                      </button>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Intitulé du poste *
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: Opérateur de production, Chauffeur PL..."
                        value={poste.intitule}
                        onChange={(e) => updatePoste(index, 'intitule', e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Taux horaire (€/h) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: 12.50"
                        value={poste.tauxHoraire}
                        onChange={(e) => updatePoste(index, 'tauxHoraire', e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Heures par semaine
                      </label>
                      <input
                        type="number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: 35"
                        value={poste.heuresParSemaine}
                        onChange={(e) => updatePoste(index, 'heuresParSemaine', e.target.value)}
                      />
                    </div>

                    {/* Heures supplémentaires */}
                    <div className="md:col-span-2">
                      <div className="flex justify-between items-center mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Heures supplémentaires
                        </label>
                        <button
                          type="button"
                          onClick={() => addTauxHS(index)}
                          className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-lg hover:bg-blue-200 transition"
                        >
                          + Ajouter une tranche
                        </button>
                      </div>

                      {poste.tauxHS.length === 0 ? (
                        <p className="text-sm text-gray-500 italic py-2">Aucune tranche d'heures supplémentaires</p>
                      ) : (
                        <div className="space-y-2">
                          {poste.tauxHS.map((hs, hsIndex) => (
                            <div key={hs.id} className="flex gap-2 items-center">
                              <input
                                type="text"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-sm"
                                placeholder="Ex: 8 premières heures"
                                value={hs.tranche}
                                onChange={(e) => updateTauxHS(index, hsIndex, 'tranche', e.target.value)}
                              />
                              <input
                                type="text"
                                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-sm"
                                placeholder="Ex: +25%"
                                value={hs.taux}
                                onChange={(e) => updateTauxHS(index, hsIndex, 'taux', e.target.value)}
                              />
                              <button
                                type="button"
                                onClick={() => removeTauxHS(index, hsIndex)}
                                className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition text-sm"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Majorations */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Majoration de nuit (%)
                      </label>
                      <input
                        type="number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: 25"
                        value={poste.majorationNuit}
                        onChange={(e) => updatePoste(index, 'majorationNuit', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Heures de nuit
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: 21h-6h"
                        value={poste.heuresNuit}
                        onChange={(e) => updatePoste(index, 'heuresNuit', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Majoration dimanche (%)
                      </label>
                      <input
                        type="number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: 50"
                        value={poste.majorationDimanche}
                        onChange={(e) => updatePoste(index, 'majorationDimanche', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Autres majorations
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: Jours fériés +100%"
                        value={poste.autresMajorations}
                        onChange={(e) => updatePoste(index, 'autresMajorations', e.target.value)}
                      />
                    </div>

                    {/* Primes */}
                    <div className="md:col-span-2">
                      <div className="flex justify-between items-center mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Primes
                        </label>
                        <button
                          type="button"
                          onClick={() => addPrime(index)}
                          className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-lg hover:bg-green-200 transition"
                        >
                          + Ajouter une prime
                        </button>
                      </div>

                      {poste.primes.length === 0 ? (
                        <p className="text-sm text-gray-500 italic py-2">Aucune prime ajoutée</p>
                      ) : (
                        <div className="space-y-3">
                          {poste.primes.map((prime, primeIndex) => (
                            <div key={prime.id} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2">
                                <input
                                  type="text"
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-sm"
                                  placeholder="Ex: Prime panier"
                                  value={prime.nom || ''}
                                  onChange={(e) => updatePrime(index, primeIndex, 'nom', e.target.value)}
                                />
                                <input
                                  type="text"
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-sm"
                                  placeholder="Ex: 150€"
                                  value={prime.montant || ''}
                                  onChange={(e) => updatePrime(index, primeIndex, 'montant', e.target.value)}
                                />
                                <select
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-sm"
                                  value={prime.frequence || ''}
                                  onChange={(e) => updatePrime(index, primeIndex, 'frequence', e.target.value)}
                                >
                                  <option value="">-- Fréquence --</option>
                                  <option value="heure">Par heure</option>
                                  <option value="semaine">Par semaine</option>
                                  <option value="mois">Par mois</option>
                                  <option value="an">Par an</option>
                                </select>
                                <select
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-sm"
                                  value={prime.assujettissement || ''}
                                  onChange={(e) => updatePrime(index, primeIndex, 'assujettissement', e.target.value)}
                                >
                                  <option value="">-- Assujettissement --</option>
                                  <option value="assujetti">Assujetti</option>
                                  <option value="non-assujetti">Non assujetti</option>
                                </select>
                              </div>
                              <button
                                type="button"
                                onClick={() => removePrime(index, primeIndex)}
                                className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition text-sm"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Avantages
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: Tickets restaurant, mutuelle, 13ème mois..."
                        value={poste.avantages}
                        onChange={(e) => updatePoste(index, 'avantages', e.target.value)}
                      />
                    </div>

                    {/* EPI */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        🦺 EPI obligatoires (Équipements de Protection Individuelle)
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                          { key: 'casque', label: '⛑️ Casque' },
                          { key: 'chaussuresSecurite', label: '👢 Chaussures de sécurité' },
                          { key: 'gants', label: '🧤 Gants' },
                          { key: 'lunettes', label: '🥽 Lunettes de protection' },
                          { key: 'masque', label: '😷 Masque respiratoire' },
                          { key: 'bouchonsOreilles', label: '🔇 Bouchons d\'oreilles' },
                          { key: 'harnais', label: '🪢 Harnais de sécurité' },
                          { key: 'giletHauteVisibilite', label: '🦺 Gilet haute visibilité' }
                        ].map(item => (
                          <label key={item.key} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              checked={poste.epi[item.key]}
                              onChange={(e) => {
                                const newEpi = { ...poste.epi, [item.key]: e.target.checked };
                                updatePoste(index, 'epi', newEpi);
                              }}
                            />
                            <span className="text-sm text-gray-700">{item.label}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Autre EPI (précisez)
                        </label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                          placeholder="Ex: Combinaison anti-acide, tablier..."
                          value={poste.epi.autre}
                          onChange={(e) => {
                            const newEpi = { ...poste.epi, autre: e.target.value };
                            updatePoste(index, 'epi', newEpi);
                          }}
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        🎓 Formation/Habilitation obligatoire pour ce poste
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: Habilitation électrique, CACES, Permis C, SST..."
                        value={poste.formationsHabilitations}
                        onChange={(e) => updatePoste(index, 'formationsHabilitations', e.target.value)}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        🔧 Utilisation de machines ou d'outillages
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        placeholder="Ex: Chariot élévateur, presse hydraulique, perceuse..."
                        value={poste.machinesOutillages}
                        onChange={(e) => updatePoste(index, 'machinesOutillages', e.target.value)}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        🏥 Surveillance médicale renforcée nécessaire ?
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        value={poste.surveillanceMedicale}
                        onChange={(e) => updatePoste(index, 'surveillanceMedicale', e.target.value)}
                      >
                        <option value="">-- Sélectionnez --</option>
                        <option value="oui">Oui</option>
                        <option value="non">Non</option>
                      </select>
                    </div>

                    {/* Pénibilité */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        ⚠️ Pénibilité du poste
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                          { key: 'milieuHyperbare', label: '🌊 Milieu hyperbare' },
                          { key: 'temperaturesExtremes', label: '🌡️ Températures extrêmes' },
                          { key: 'bruit', label: '🔊 Bruit' },
                          { key: 'travailNuit', label: '🌙 Travail de nuit' },
                          { key: 'equipesSuccessives', label: '🔄 Équipes successives alternantes' },
                          { key: 'travailRepetitif', label: '🔁 Travail répétitif' }
                        ].map(item => (
                          <label key={item.key} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-indigo-50 transition bg-white">
                            <input
                              type="checkbox"
                              checked={poste.penibilite[item.key]}
                              onChange={() => updatePenibilite(index, item.key)}
                              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                            />
                            <span className="text-sm">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Message d'avertissement conditionnel */}
                    {(
                      Object.entries(poste.epi).some(([k, v]) => (k !== 'autre' && v) || (k === 'autre' && v)) ||
                      poste.formationsHabilitations ||
                      poste.machinesOutillages ||
                      poste.surveillanceMedicale === 'oui' ||
                      Object.values(poste.penibilite).some(v => v)
                    ) && (
                      <div className="md:col-span-2 mt-4">
                        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-lg">
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">⚠️</span>
                            <div>
                              <h4 className="font-bold text-orange-800 mb-2">
                                Obligation de formation sécurité
                              </h4>
                              <p className="text-sm text-orange-900 leading-relaxed">
                                Les salariés intérimaires doivent bénéficier, dès leur arrivée, du même niveau de protection en santé et sécurité que les autres salariés. L'entreprise utilisatrice, responsable des conditions de travail, doit leur dispenser une formation pratique et adaptée à la sécurité (art. L. 4141-2 du Code du travail), afin qu'ils connaissent les gestes, règles et comportements nécessaires pour assurer leur sécurité et celle des autres.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-center mt-6">
                <button
                  type="button"
                  onClick={addPoste}
                  className="bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition flex items-center gap-2 text-lg font-medium"
                >
                  <span className="text-xl">+</span> Ajouter un poste
                </button>
              </div>
            </div>
          )}

          {/* ÉTAPE 3: Validation */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">✓ Récapitulatif</h2>

              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                <h3 className="font-semibold text-lg text-indigo-900 mb-3">🏢 Entreprise</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Nom:</strong> {formData.nomEntreprise}</p>
                  <p><strong>SIRET:</strong> {formData.siret}</p>
                  {formData.formeJuridique && <p><strong>Forme juridique:</strong> {formData.formeJuridique}</p>}
                  {(formData.adresse || formData.codePostal || formData.ville) && (
                    <p><strong>Adresse siège:</strong> {[formData.adresse, formData.codePostal, formData.ville].filter(Boolean).join(', ')}</p>
                  )}
                  {(formData.adresseFacturation || formData.codePostalFacturation || formData.villeFacturation) && (
                    <p><strong>Adresse facturation:</strong> {[formData.adresseFacturation, formData.codePostalFacturation, formData.villeFacturation].filter(Boolean).join(', ')}</p>
                  )}
                  {formData.tvaIntracommunautaire && <p><strong>TVA intracommunautaire:</strong> {formData.tvaIntracommunautaire}</p>}
                  {formData.conventionCollective && <p><strong>Convention collective:</strong> {formData.conventionCollective}</p>}
                  {formData.centreVisiteMedicale && <p><strong>Centre visite médicale:</strong> {formData.centreVisiteMedicale}</p>}
                  {formData.contactNom && <p><strong>Contact:</strong> {formData.contactNom}</p>}
                  {formData.contactPoste && <p><strong>Poste:</strong> {formData.contactPoste}</p>}
                  <p><strong>Email:</strong> {formData.contactEmail}</p>
                  {formData.contactTelephone && <p><strong>Tél:</strong> {formData.contactTelephone}</p>}
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h3 className="font-semibold text-lg text-purple-900 mb-3">💼 Postes ({formData.postes.length})</h3>
                {formData.postes.map((poste, index) => (
                  <div key={poste.id} className="mb-4 pb-4 border-b border-purple-200 last:border-0">
                    <p className="font-semibold text-purple-900">#{index + 1} - {poste.intitule}</p>
                    <div className="text-sm mt-2 space-y-1">
                      <p><strong>Taux horaire:</strong> {poste.tauxHoraire}€/h</p>
                      <p><strong>Heures/semaine:</strong> {poste.heuresParSemaine}h</p>
                      {poste.tauxHS.length > 0 && (
                        <div>
                          <strong>Heures supplémentaires:</strong>
                          <ul className="ml-4 mt-1">
                            {poste.tauxHS.map((hs, idx) => (
                              <li key={idx}>• {hs.tranche} : {hs.taux}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {poste.majorationNuit && <p><strong>Majoration nuit:</strong> {poste.majorationNuit}%{poste.heuresNuit && ` (${poste.heuresNuit})`}</p>}
                      {poste.majorationDimanche && <p><strong>Majoration dimanche:</strong> {poste.majorationDimanche}%</p>}
                      {poste.autresMajorations && <p><strong>Autres majorations:</strong> {poste.autresMajorations}</p>}
                      {poste.primes.length > 0 && (
                        <div>
                          <strong>Primes:</strong>
                          <ul className="ml-4 mt-1">
                            {poste.primes.map((prime, pIdx) => (
                              <li key={pIdx}>
                                • {prime.nom || 'Prime'}{prime.montant && ` : ${prime.montant}`}
                                {prime.frequence && ` (${
                                  prime.frequence === 'heure' ? 'par heure' :
                                  prime.frequence === 'semaine' ? 'par semaine' :
                                  prime.frequence === 'mois' ? 'par mois' :
                                  prime.frequence === 'an' ? 'par an' : ''
                                })`}
                                {prime.assujettissement && ` - ${
                                  prime.assujettissement === 'assujetti' ? 'Assujetti' :
                                  prime.assujettissement === 'non-assujetti' ? 'Non assujetti' : ''
                                }`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {poste.avantages && <p><strong>Avantages:</strong> {poste.avantages}</p>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Signature */}
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mt-6">
                <h3 className="text-lg font-bold text-blue-900 mb-4">
                  ✍️ Attestation de véracité
                </h3>
                <p className="text-sm text-blue-900 mb-4 leading-relaxed">
                  Je soussigné(e), certifie sur l'honneur l'exactitude des informations fournies dans ce formulaire.
                  Je reconnais que toute fausse déclaration pourra entraîner des conséquences juridiques et la résiliation
                  des relations contractuelles.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      Nom et prénom du signataire *
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      placeholder="Ex: Jean Dupont"
                      value={formData.signatureNom || ''}
                      onChange={(e) => setFormData({...formData, signatureNom: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      Fonction *
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      placeholder="Ex: Directeur des Ressources Humaines"
                      value={formData.signatureFonction || ''}
                      onChange={(e) => setFormData({...formData, signatureFonction: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      Date *
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      value={formData.signatureDate || new Date().toISOString().split('T')[0]}
                      onChange={(e) => setFormData({...formData, signatureDate: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      Signature (tapez votre nom) *
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white italic text-xl"
                      placeholder="Votre signature"
                      value={formData.signature || ''}
                      onChange={(e) => setFormData({...formData, signature: e.target.value})}
                      required
                      style={{fontFamily: 'cursive'}}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mt-6">
                <p className="text-sm text-blue-900 font-medium mb-2">
                  📧 Envoi automatique
                </p>
                <p className="text-sm text-blue-800">
                  En cliquant sur "Envoyer", vos informations seront enregistrées et envoyées à J'M Interim.
                  Vous recevrez une confirmation par email à <strong>{formData.contactEmail}</strong>.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-lg font-medium transition ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ← Précédent
            </button>

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition"
              >
                Suivant →
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-8 py-3 rounded-lg font-medium transition shadow-lg ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Envoi en cours...
                  </>
                ) : (
                  '✓ Envoyer'
                )}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-600 text-sm">
          <p>© 2024 J'M Interim - Tous droits réservés</p>
          <p className="mt-2">
            Des questions ? Contactez-nous à{' '}
            <a href="mailto:contact@jm-interim.com" className="text-indigo-600 hover:underline">
              contact@jm-interim.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FicheClientForm;


